import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, PresentationIcon } from 'lucide-react';
import { ClosingLine, InteriorSlide, SlideTitle } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { SnakStill } from '../SnakStill';
import { NordicCaseOverlay } from '../nordicCase/NordicCaseOverlay';
import { experiencedSteps } from '../../data/snakkerStory';
export function SlideExperienced({}: SlideProps) {
  const [caseOpen, setCaseOpen] = useState(false);
  return <InteriorSlide data-id="element-414" data-mp="1vxqa5uszo4gs">
      <div className="flex flex-shrink-0 items-end justify-between gap-8" data-id="element-415" data-mp="1vx6zmz5o4ics">
        <SlideTitle eyebrow="What you just experienced" data-id="element-416" data-mp="1r4x5t81fmy58">
          Short. Sharp. <span className="text-[var(--brand)]" data-id="element-417" data-mp="1m7434ozc8m9p">Rewarding.</span>
        </SlideTitle>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3,
        duration: 0.5
      }} className="hidden max-w-sm pb-1 text-[clamp(0.82rem,1.05vw,1rem)] font-medium leading-snug text-[var(--muted)] lg:block" data-id="element-418" data-mp="2jm9j8cjv4l9x">
          For this prototype we used individualized first-cycle dosing as the example. The more important idea is the format: turn an
          important product message into something the HCP actively experiences.
        </motion.p>
      </div>

      <ol className="mt-[clamp(1.25rem,2.5vw,2.25rem)] flex min-h-0 flex-1 items-stretch" aria-label="The Snak journey" data-id="element-419" data-mp="n7dudfkfq0ph">
        {experiencedSteps.map((step, i) => <React.Fragment key={step.label} data-id="element-420" data-mp="e7h9zq52vo08" data-mpi={i}>
            <motion.li initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15 + i * 0.09,
          duration: 0.45
        }} className="flex min-h-0 min-w-0 flex-1 flex-col" data-id="element-421" data-mp="e8ipq7x1gyoa">
              <p className={`flex-shrink-0 text-[clamp(0.6rem,0.82vw,0.76rem)] font-bold uppercase tracking-[0.18em] ${step.highlight ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-422" data-mp="1um0frjgaubl5">
                <span className="text-[var(--muted)]" data-id="element-423" data-mp="37lu5d2wwmjnf">{String(i + 1).padStart(2, '0')}</span> {step.label}
              </p>
              <div className={`mt-2 h-px flex-shrink-0 ${step.highlight ? 'bg-[var(--brand)]' : 'bg-[var(--fg)]/15'}`} data-id="element-424" data-mp="32nagatn0krqu" />
              <SnakStill screen={step.screen} alt={`${step.label} — REKOVELLE Snak screen`} className="mt-[clamp(0.6rem,1.2vw,1rem)] w-full flex-1" data-id="element-425" data-mp="2tkri4mu6b3ax" />
              <p className="mt-[clamp(0.5rem,1vw,0.8rem)] flex-shrink-0 text-center text-[clamp(0.72rem,0.95vw,0.9rem)] font-semibold leading-snug text-[var(--fg)]" data-id="element-426" data-mp="3qa8wdq781xo8">
                {step.detail}
              </p>
            </motion.li>
            {i < experiencedSteps.length - 1 && <li aria-hidden="true" className="flex w-[clamp(1rem,2vw,2rem)] flex-shrink-0 items-center justify-center" data-id="element-427" data-mp="3ciklfq8plf8u">
                <ArrowRightIcon className="h-4 w-4 text-[var(--brand-light)]" data-id="element-428" data-mp="duojms4lhdv9" />
              </li>}
          </React.Fragment>)}
      </ol>

      <div className="flex items-end justify-between gap-6" data-id="element-429" data-mp="gel7c3xyubjb">
        <div className="min-w-0 flex-1" data-id="element-430" data-mp="gg4n7tfpmkkk">
          <ClosingLine data-id="element-431" data-mp="ldxrggxgd4i7">
            The specific creative idea can change. The principle does not:{' '}
            <span className="text-[var(--brand)]" data-id="element-432" data-mp="qawhoodzwrb4">make the benefit interactive.</span>
          </ClosingLine>
        </div>
        <button type="button" onClick={() => setCaseOpen(true)} className="mb-1 hidden flex-shrink-0 items-center gap-1.5 rounded-full border border-[var(--fg)]/15 px-3.5 py-1.5 text-[clamp(0.66rem,0.85vw,0.8rem)] font-bold text-[var(--fg)] transition-colors hover:border-[var(--fg)]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] lg:inline-flex hide-on-print" data-id="element-433" data-mp="uf47yq03qfbg">
          <PresentationIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-434" data-mp="1i35fbvljnppa" />
          Proof: a Nordic Snak case
        </button>
      </div>
      <NordicCaseOverlay open={caseOpen} onClose={() => setCaseOpen(false)} data-id="element-435" data-mp="1n1glnwcmri2r" />
    </InteriorSlide>;
}